import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { requestMeRefresh, useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import { updateMe, updateMeProfile, listEducation, createEducation, updateEducation, deleteEducation, listEmployment, createEmployment, updateEmployment, deleteEmployment, getSkills, seedSkills, replaceSkills } from '../../api/me'
import { listMyAppeals, type AppealItem } from '../../api/moderation'
import { checkHandle } from '../../api/handles'
import { uploadAvatar, publishAvatar } from '../../api/avatars'
import { getWalletNFTs, type WalletNFTItem } from '../../api/wallet'
import AppealModal from '../../components/moderation/AppealModal'

export default function SettingsProfile() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const [displayName, setDisplayName] = useState('')
  const [handle, setHandle] = useState('')
  const [motto, setMotto] = useState('')
  const [livesIn, setLivesIn] = useState('')
  const [fromLocation, setFromLocation] = useState('')
  const [handleTier, setHandleTier] = useState<string | null>(null)
  const [checkingHandle, setCheckingHandle] = useState(false)
  const [ownedHandles, setOwnedHandles] = useState<Array<{ handle: string; image_url?: string }>>([])
  const [handlePreviewOpen, setHandlePreviewOpen] = useState(false)
  const [handlePreviewUrl, setHandlePreviewUrl] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string>('')
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saveFeedback, setSaveFeedback] = useState<{
    profile_state?: string
    moderation_action?: string
    moderation_reasons?: string[]
    suggested_fixes?: string[]
  } | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const moderationStatus = me?.profile?.profile_visibility_status || null
  const moderationAction = saveFeedback?.moderation_action || me?.profile?.moderation_action || 'none'
  const effectiveProfileState =
    saveFeedback?.profile_state ||
    me?.profile?.profile_state ||
    (moderationStatus === 'requires_edit' ? 'needs_edits' : moderationStatus === 'public' ? 'published' : 'draft')
  const moderationReasons = saveFeedback?.moderation_reasons || me?.profile?.moderation_reasons || []
  const suggestedFixes = saveFeedback?.suggested_fixes || me?.profile?.suggested_fixes || []
  const moderationMessage =
    me?.profile?.user_safe_message ||
    me?.profile?.profile_visibility_message ||
    (effectiveProfileState === 'needs_edits'
      ? 'Your profile needs edits before it can be published.'
      : 'Your profile is hidden pending review.')
  const showWarningPanel = moderationAction === 'warn'
  const [appealOpen, setAppealOpen] = useState(false)
  const [appealStatus, setAppealStatus] = useState<AppealItem | null>(null)
  const [appealsLoading, setAppealsLoading] = useState(false)

  // Education / Employment / Skills
  const [education, setEducation] = useState<any[] | null>(null)
  const [employment, setEmployment] = useState<any[] | null>(null)
  const [skills, setSkills] = useState<any[] | null>(null)
  const [newSkill, setNewSkill] = useState('')
  const [edDraft, setEdDraft] = useState<any>({ degree: '', org: '', start_year: undefined, end_year: undefined, location: '', summary: '' })
  const [jobDraft, setJobDraft] = useState<any>({ title: '', company: '', start_year: undefined, end_year: undefined, location: '', summary: '' })

  useEffect(() => {
    if (!me) return
    setDisplayName(me.profile?.display_name || '')
    setHandle(me.profile?.handle || me.username || '')
    setMotto(me.profile?.motto || '')
    setLivesIn(me.profile?.lives_in_label || '')
    setFromLocation(me.profile?.from_location_label || '')
    setAvatarUrl(me.profile?.avatar_url || '')
    if (me.profile?.handle) {
      setCheckingHandle(true)
      ;(async () => {
        try {
          const res = await checkHandle(me.profile?.handle || '')
          setHandleTier((res.tier || '').toLowerCase())
        } catch {
          setHandleTier(null)
        } finally {
          setCheckingHandle(false)
        }
      })()
    } else {
      setHandleTier(null)
    }
    // Load sections
    ;(async () => {
      try {
        const [ed, jobs, sk] = await Promise.all([
          listEducation().catch(()=>[]),
          listEmployment().catch(()=>[]),
          getSkills().catch(()=>[]),
        ])
        setEducation(ed)
        setEmployment(jobs)
        setSkills(sk)
        setNewSkill('')
      } catch {}
    })()

    // Load owned handle NFTs for dropdown + preview (best-effort).
    ;(async () => {
      try {
        const items: WalletNFTItem[] = []
        let cursor: string | null | undefined = undefined
        // Pull a few pages max to keep it snappy.
        for (let i = 0; i < 5; i += 1) {
          const p = await getWalletNFTs(cursor || undefined)
          items.push(...(p.items || []))
          cursor = p.next
          if (!cursor) break
        }
        const handles = items
          .map((it) => ({ handle: (it.handle || it.title || '').replace(/^@/, '').trim(), image_url: it.image_url }))
          .filter((x) => x.handle.length > 0)

        // Deduplicate by handle name, prefer the first occurrence.
        const seen = new Set<string>()
        const uniq: Array<{ handle: string; image_url?: string }> = []
        for (const h of handles) {
          const key = h.handle.toLowerCase()
          if (seen.has(key)) continue
          seen.add(key)
          uniq.push(h)
        }
        setOwnedHandles(uniq)

        // If current handle matches one we own, set preview url.
        const current = (me.profile?.handle || '').replace(/^@/, '').trim().toLowerCase()
        const match = uniq.find((u) => u.handle.toLowerCase() === current)
        if (match?.image_url) setHandlePreviewUrl(match.image_url)
      } catch {
        setOwnedHandles([])
      }
    })()
  }, [me])

  useEffect(() => {
    setAvatarLoadFailed(false)
  }, [avatarUrl])

  // Keep handle tier in sync with the currently selected handle (best-effort).
  useEffect(() => {
    if (!me) return
    const h = (handle || '').replace(/^@/, '').trim()
    if (!h) {
      setHandleTier(null)
      return
    }
    let cancelled = false
    setCheckingHandle(true)
    checkHandle(h)
      .then((res) => {
        if (cancelled) return
        setHandleTier((res.tier || '').toLowerCase())
      })
      .catch(() => {
        if (!cancelled) setHandleTier(null)
      })
      .finally(() => {
        if (!cancelled) setCheckingHandle(false)
      })
    return () => { cancelled = true }
  }, [me?.id, handle])

  // Keep preview url synced when selected handle changes.
  useEffect(() => {
    const current = (handle || '').replace(/^@/, '').trim().toLowerCase()
    const match = ownedHandles.find((u) => u.handle.toLowerCase() === current)
    setHandlePreviewUrl(match?.image_url || '')
  }, [handle, ownedHandles])

  useEffect(() => {
    if (!handlePreviewOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHandlePreviewOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handlePreviewOpen])

  useEffect(() => {
    if (!authed || !me?.profile?.last_moderation_event_id) {
      setAppealStatus(null)
      return
    }
    if (moderationStatus !== 'hidden_pending_review' && effectiveProfileState !== 'needs_edits') {
      setAppealStatus(null)
      return
    }
    let cancelled = false
    setAppealsLoading(true)
    listMyAppeals()
      .then(({ items }) => {
        if (cancelled) return
        const match = items.find((item) => item.event_id === me.profile.last_moderation_event_id) || null
        setAppealStatus(match)
      })
      .catch(() => {
        if (!cancelled) setAppealStatus(null)
      })
      .finally(() => {
        if (!cancelled) setAppealsLoading(false)
      })
    return () => { cancelled = true }
  }, [authed, me?.profile?.last_moderation_event_id, moderationStatus, effectiveProfileState])

  async function onSave() {
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      // Profile surface
      const profileResp = await updateMeProfile({
        display_name: displayName,
        handle: (handle || '').replace(/^@/, '').trim(),
        motto,
        lives_in_label: livesIn,
        from_location_label: fromLocation,
      })
      setSaveFeedback({
        profile_state: profileResp?.profile_state,
        moderation_action: profileResp?.moderation_action,
        moderation_reasons: profileResp?.moderation_reasons || [],
        suggested_fixes: profileResp?.suggested_fixes || [],
      })
      requestMeRefresh()
      setMessage('Saved')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function onPickAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    try {
      const upload = await uploadAvatar(f)
      const published = await publishAvatar(upload.asset_id)
      setAvatarUrl(published?.profile?.avatar_url || '')
      requestMeRefresh()
      setMessage('Avatar updated')
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || e?.message || 'Avatar upload failed')
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-extrabold">Profile</h1>
        <div className="flex items-center gap-2">
          <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {(moderationStatus === 'hidden_pending_review' || effectiveProfileState === 'needs_edits' || showWarningPanel) && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            showWarningPanel
              ? 'border-sky-400/40 bg-sky-500/10 text-sky-100'
              : effectiveProfileState === 'needs_edits'
              ? 'border-amber-400/40 bg-amber-500/10 text-amber-100'
              : 'border-blue-400/40 bg-blue-500/10 text-blue-100'
          }`}
        >
          {showWarningPanel ? (
            <div className="space-y-2">
              <div className="font-semibold">Double-check your profile links</div>
              <div className="text-white/85">
                Your profile saved, but one or more links may look suspicious to viewers.
              </div>
              {moderationReasons.length > 0 ? (
                <ul className="list-disc pl-5 text-white/80">
                  {moderationReasons.map((reason: string) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
              {suggestedFixes.length > 0 ? (
                <ul className="list-disc pl-5 text-white/80">
                  {suggestedFixes.map((fix: string) => (
                    <li key={fix}>{fix}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : effectiveProfileState === 'needs_edits' ? (
            <div className="space-y-2">
              <div className="font-semibold">Profile needs edits to be public</div>
              <div className="text-white/85">{moderationMessage}</div>
              {moderationReasons.length > 0 ? (
                <ul className="list-disc pl-5 text-white/80">
                  {moderationReasons.map((reason: string) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
              {suggestedFixes.length > 0 ? (
                <ul className="list-disc pl-5 text-white/80">
                  {suggestedFixes.map((fix: string) => (
                    <li key={fix}>{fix}</li>
                  ))}
                </ul>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-white/25 px-3 py-1 text-xs font-semibold text-white hover:bg-white/10"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Edit now
                </button>
                <span className="text-xs text-white/75">You can keep saving and continue using Pixeloop. Your profile stays owner-only until fixed.</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>{moderationMessage}</div>
              <div className="flex items-center gap-2">
                {appealStatus?.status === 'open' ? (
                  <span className="rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">Appeal submitted</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAppealOpen(true)}
                    className="rounded-lg border border-white/20 px-3 py-1 text-xs font-semibold text-white/90 hover:bg-white/10"
                    disabled={appealsLoading}
                  >
                    Appeal
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {appealStatus && appealStatus.status !== 'open' && (
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
          <div className="font-semibold">
            {appealStatus.status === 'accepted' ? 'Appeal accepted.' : 'Appeal denied.'}
          </div>
          {appealStatus.user_note && <div className="mt-1 text-white/70">{appealStatus.user_note}</div>}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          {message}
        </div>
      )}

      {handlePreviewOpen && handlePreviewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setHandlePreviewOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-white/90 truncate pr-8">
                @{(handle || '').replace(/^@/, '').trim()}
              </div>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border border-white/15 text-white/90 hover:bg-white/10"
                onClick={() => setHandlePreviewOpen(false)}
              >
                Close
              </button>
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={handlePreviewUrl}
              alt={`@${(handle || '').replace(/^@/, '').trim()} card`}
              className="w-full max-h-[80vh] object-contain rounded-xl border border-white/10 bg-white/5"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Identity</div>
            {handleTier && handleTier === 'standard' && (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-100">
                <div>Upgrade to a premium handle for rarity and resale perks.</div>
                <a href="/settings/handles" className="inline-flex items-center rounded-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-black hover:bg-amber-400">
                  Check premium handles
                </a>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-sm text-white/70 mb-1">Display name</div>
                <input value={displayName} onChange={(e)=>setDisplayName(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand" />
              </label>
		              <label className="block">
		                <div className="text-sm text-white/70 mb-1">Handle</div>
		                <div className="flex">
		                  <button
		                    type="button"
		                    className="inline-flex items-center px-3 rounded-l-lg border border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
		                    disabled={!handlePreviewUrl}
		                    onClick={() => {
		                      if (handlePreviewUrl) setHandlePreviewOpen(true)
		                    }}
		                    title={handlePreviewUrl ? 'View handle card' : 'No handle card available yet'}
		                  >
		                    @
		                  </button>
		                  <div className="relative flex-1">
		                    {ownedHandles.length > 0 ? (
		                      <select
		                        value={(handle || '').replace(/^@/, '').trim()}
		                        onChange={(e) => setHandle(e.target.value)}
		                        className="w-full rounded-r-lg bg-white/5 border border-white/10 px-3 py-2 text-white/90 outline-none focus:ring-2 focus:ring-brand cursor-pointer"
		                        style={{ colorScheme: 'dark' } as any}
		                        onDoubleClick={() => {
		                          if (handlePreviewUrl) setHandlePreviewOpen(true)
		                        }}
		                      >
		                          {(() => {
		                            const current = (handle || '').replace(/^@/, '').trim()
		                            const opts = ownedHandles.map((h) => h.handle)
		                            const hasCurrent = current && opts.some((x) => x.toLowerCase() === current.toLowerCase())
	                            const all = hasCurrent || !current ? opts : [current, ...opts]
	                            return all.map((h) => (
	                              <option key={h} value={h} style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>
	                                {h}
	                              </option>
	                            ))
	                          })()}
		                      </select>
		                    ) : (
		                      <input
		                        value={(handle || '').replace(/^@/, '').trim()}
		                        readOnly
	                        className="w-full rounded-r-lg bg-white/5 border border-white/10 px-3 py-2 text-white/70 outline-none"
	                        onClick={() => {
	                          if (handlePreviewUrl) setHandlePreviewOpen(true)
	                        }}
	                      />
	                    )}
	                  </div>
	                </div>
	                <div className="mt-1 text-xs text-white/60">
	                  Select which handle is shown on your profile. Need a new handle?{' '}
	                  <a href="/settings/handles" className="text-amber-300 hover:text-amber-200">Open handle registry</a>
	                </div>
	              </label>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Bio</div>
            <label className="block mb-3">
              <div className="text-sm text-white/70 mb-1">Tagline</div>
              <input value={motto} onChange={(e)=>setMotto(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand" />
            </label>
            <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">People bio & cover</div>
                <div className="text-xs text-white/70">Edit your public bio, story, and cover in People Editor.</div>
              </div>
              <Link to="/people/edit/new" className="inline-flex items-center rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600">
                Open People Editor
              </Link>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Location</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-sm text-white/70 mb-1">Lives in</div>
                <input value={livesIn} onChange={(e)=>setLivesIn(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand" />
              </label>
              <label className="block">
                <div className="text-sm text-white/70 mb-1">From</div>
                <input value={fromLocation} onChange={(e)=>setFromLocation(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand" />
              </label>
            </div>
          </div>

          {/* Skills */}
          <div className="rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Skills</div>
              <div className="text-xs text-white/60">Add the technologies you use</div>
            </div>

            {/* Current skills as chips with delete */}
            {skills && skills.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {skills.map((s: any, idx: number) => (
                  <span key={s.id || s.skill_id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 border border-white/15 text-xs">
                    {s.name || 'Skill'}
                    <button
                      aria-label={`Remove ${s.name}`}
                      className="ml-1 hover:text-red-300"
                      onClick={async () => {
                        try {
                          const remaining = (skills || []).filter((x: any) => (x.id || x.skill_id) !== (s.id || s.skill_id))
                          // Persist via replace
                          const payload = remaining.map((item: any, i: number) => ({
                            skill_id: item.skill_id || item.id,
                            proficiency: item.proficiency || '',
                            years_experience: item.years_experience || null,
                            last_used_year: item.last_used_year || null,
                            position: i,
                          }))
                          await replaceSkills(payload)
                          const fresh = await getSkills()
                          setSkills(fresh)
                        } catch (e: any) {
                          setMessage(e?.response?.data?.detail || e?.message || 'Failed to remove skill')
                        }
                      }}
                    >×</button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-3 text-sm text-white/70">No skills yet.</div>
            )}

            {/* Add skill row */}
            <div className="mt-4 flex gap-2">
              <input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={async (e) => { if (e.key === 'Enter') { e.preventDefault(); (document.getElementById('add-skill-btn') as HTMLButtonElement)?.click() } }}
                placeholder="Add a skill (e.g., React)"
                className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand"
              />
              <button
                id="add-skill-btn"
                className="px-3 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-60"
                disabled={!newSkill.trim()}
                onClick={async () => {
                  const name = newSkill.trim()
                  if (!name) return
                  setMessage(null)
                  try {
                    const seeded = await seedSkills([name])
                    const skillId = (seeded.results && seeded.results[0]?.id) || null
                    if (!skillId) throw new Error('Skill create failed')
                    const next = [...(skills || []), { skill_id: skillId, name }]
                    const payload = next.map((item: any, i: number) => ({
                      skill_id: item.skill_id || item.id,
                      proficiency: item.proficiency || '',
                      years_experience: item.years_experience || null,
                      last_used_year: item.last_used_year || null,
                      position: i,
                    }))
                    await replaceSkills(payload)
                    const fresh = await getSkills()
                    setSkills(fresh)
                    setNewSkill('')
                    setMessage('Skill added')
                  } catch (e: any) {
                    setMessage(e?.response?.data?.detail || e?.message || 'Failed to add skill')
                  }
                }}
              >Add</button>
            </div>
          </div>

          {/* Education */}
          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Education</div>
            {education?.map((e:any)=> (
              <div key={e.id} className="mb-3 rounded-lg border border-white/10 p-3">
                <div className="text-sm font-semibold">{e.degree || 'Degree'} — {e.org || ''}</div>
                <div className="text-xs text-white/70">{e.start_year || ''}{e.end_year?` – ${e.end_year}`:''} {e.location?`• ${e.location}`:''}</div>
                {e.summary && <div className="text-sm mt-1 whitespace-pre-wrap">{e.summary}</div>}
                <div className="mt-2 flex gap-2 text-xs">
                  <button onClick={async ()=>{ await deleteEducation(e.id); setEducation((arr)=>arr?.filter((x:any)=>x.id!==e.id) || null) }} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Delete</button>
                </div>
              </div>
            ))}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={edDraft.degree} onChange={(ev)=>setEdDraft({...edDraft, degree: ev.target.value})} placeholder="Degree" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={edDraft.org} onChange={(ev)=>setEdDraft({...edDraft, org: ev.target.value})} placeholder="School / Organization" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={edDraft.start_year||''} onChange={(ev)=>setEdDraft({...edDraft, start_year: parseInt(ev.target.value)||undefined})} placeholder="Start year" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={edDraft.end_year||''} onChange={(ev)=>setEdDraft({...edDraft, end_year: parseInt(ev.target.value)||undefined})} placeholder="End year" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={edDraft.location} onChange={(ev)=>setEdDraft({...edDraft, location: ev.target.value})} placeholder="Location" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={edDraft.summary} onChange={(ev)=>setEdDraft({...edDraft, summary: ev.target.value})} placeholder="Summary" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none md:col-span-2" />
            </div>
            <div className="mt-2">
              <button onClick={async ()=>{ const res=await createEducation(edDraft); const fresh=await listEducation(); setEducation(fresh); setEdDraft({ degree:'', org:'', start_year: undefined, end_year: undefined, location:'', summary:'' }); }} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 hover:bg-white/15 text-sm">Add education</button>
            </div>
          </div>

          {/* Employment */}
          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Employment</div>
            {employment?.map((j:any)=> (
              <div key={j.id} className="mb-3 rounded-lg border border-white/10 p-3">
                <div className="text-sm font-semibold">{j.title || 'Title'} — {j.company || ''}</div>
                <div className="text-xs text-white/70">{j.start_year || ''}{j.end_year?` – ${j.end_year}`:''} {j.location?`• ${j.location}`:''}</div>
                {j.summary && <div className="text-sm mt-1 whitespace-pre-wrap">{j.summary}</div>}
                <div className="mt-2 flex gap-2 text-xs">
                  <button onClick={async ()=>{ await deleteEmployment(j.id); setEmployment((arr)=>arr?.filter((x:any)=>x.id!==j.id) || null) }} className="px-2 py-1 rounded border border-white/15 hover:bg-white/5">Delete</button>
                </div>
              </div>
            ))}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={jobDraft.title} onChange={(ev)=>setJobDraft({...jobDraft, title: ev.target.value})} placeholder="Title" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={jobDraft.company} onChange={(ev)=>setJobDraft({...jobDraft, company: ev.target.value})} placeholder="Company" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={jobDraft.start_year||''} onChange={(ev)=>setJobDraft({...jobDraft, start_year: parseInt(ev.target.value)||undefined})} placeholder="Start year" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={jobDraft.end_year||''} onChange={(ev)=>setJobDraft({...jobDraft, end_year: parseInt(ev.target.value)||undefined})} placeholder="End year" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={jobDraft.location} onChange={(ev)=>setJobDraft({...jobDraft, location: ev.target.value})} placeholder="Location" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              <input value={jobDraft.summary} onChange={(ev)=>setJobDraft({...jobDraft, summary: ev.target.value})} placeholder="Summary" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none md:col-span-2" />
            </div>
            <div className="mt-2">
              <button onClick={async ()=>{ const res=await createEmployment(jobDraft); const fresh=await listEmployment(); setEmployment(fresh); setJobDraft({ title:'', company:'', start_year: undefined, end_year: undefined, location:'', summary:'' }); }} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 hover:bg-white/15 text-sm">Add employment</button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 p-4 text-center">
            <div className="font-semibold mb-2">Avatar</div>
            <div className="mx-auto h-24 w-24 rounded-full overflow-hidden border border-white/10 bg-white/5">
              {avatarUrl && !avatarLoadFailed ? (
                <img src={avatarUrl} className="h-full w-full object-cover" onError={() => setAvatarLoadFailed(true)} />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xl text-white/60">
                  {(displayName || handle || '?').trim().charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="mt-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" />
              <button onClick={()=>fileRef.current?.click()} className="px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/5 text-sm">Upload new</button>
            </div>
            <div className="mt-2 text-xs text-white/60">JPG/PNG/WebP. Auto‑oriented, EXIF‑stripped.</div>
          </div>
          {message && <div className="rounded-lg border border-white/10 p-2 text-sm text-white/85">{message}</div>}
        </div>
      </div>
      <AppealModal
        open={appealOpen}
        onClose={() => setAppealOpen(false)}
        onSuccess={() => setAppealStatus({ appeal_id: '', event_id: me?.profile?.last_moderation_event_id, content_type: 'profile', content_id: '', created_at: new Date().toISOString(), status: 'open' })}
        contentType="profile"
        contentId={me?.profile?.public_id || ''}
        title="Appeal profile moderation"
      />
    </div>
  )
}
